import {useTranslation} from "react-i18next";
import {Termination, TerminationField, TerminationMailType, TerminationStatus} from "../../app/api/model/termination";
import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import {RoutePath} from "../../types";
import {DateFormat, formatTimestamp} from "../../utils/datetime";
import Page from "../../components/Layout/Page";
import {TranslationContext, getTranslation} from "../../utils/getTranslationKey";
import {getMailCellContent, getTerminationStatusCellContent} from "./helpers";
import {useTerminationListQuery} from "../../app/api/api";
import {CustomerNameField} from "../orders/components/CustomerNameField";
import React from "react";

export default function TerminationList() {
    const {t} = useTranslation();
    const columnDefs: ColumnDef<Termination>[] = [
        {
            fieldName: TerminationField.ID,
            align: "left",
            label: t("generic_id"),
            width: 75,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.TERMINATIONS),
            sticky: true,
        },
        {
            fieldName: TerminationField.CUSTOMER_NUMBER,
            align: "left",
            label: t("generic_customerNumber"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: TerminationField.CUSTOMER_NAME,
            align: "left",
            label: t("generic_customerName"),
            width: 200,
            sortable: true,
            formatCellText: (param) => ({
                node: (
                    <CustomerNameField
                        customerName={param.cellValue}
                        customerNameMismatch={param.row[TerminationField.IS_CORRUPTED]}
                        validationError={
                            param.row[TerminationField.IS_CORRUPTED] ? t("info_corruptedTermination") : undefined
                        }
                    />
                ),
            }),
        },
        {
            fieldName: TerminationField.TERMINATION_AT,
            align: "left",
            label: t("termination_terminationAt"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue, DateFormat.DATE);
            },
        },
        {
            fieldName: TerminationField.IS_FULL_TERMINATION,
            align: "left",
            label: t("termination_isFullTermination"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) => {
                return row[TerminationField.IS_FULL_TERMINATION] ? t("generic_yes") : t("generic_no");
            },
        },
        {
            fieldName: TerminationField.DEVICES_PENDING,
            align: "left",
            label: t("termination_devicesPending"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: TerminationField.DEVICES_TOTAL,
            align: "left",
            label: t("termination_devicesTotal"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: TerminationField.STATUS,
            align: "left",
            label: t("generic_status"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) => getTerminationStatusCellContent({status: row[TerminationField.STATUS], t}),
        },
        {
            fieldName: TerminationField.STATUS_CHANGED_AT,
            align: "left",
            label: t("termination_statusChangedAt"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: TerminationField.MAIL_PENDING,
            align: "center",
            label: t("termination_mailPending"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) =>
                getMailCellContent({
                    mailType: row[TerminationField.MAIL_PENDING],
                    mailAt: row[TerminationField.MAIL_AT],
                    t,
                }) ?? "",
        },
    ];
    const filterDefs: FilterDef<Termination>[] = [
        {
            fieldName: TerminationField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: TerminationField.CUSTOMER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationField.CUSTOMER_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: TerminationField.TERMINATION_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: TerminationField.IS_FULL_TERMINATION,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: [
                {value: "true", labelKey: t("generic_yes")},
                {value: "false", labelKey: t("generic_no")},
            ],
            type: FilterType.ENUM,
        },
        {
            fieldName: TerminationField.DEVICES_PENDING,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.NUMBER,
        },
        {
            fieldName: TerminationField.DEVICES_TOTAL,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.NUMBER,
        },
        {
            fieldName: TerminationField.STATUS,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.values(TerminationStatus).map((status) => ({
                value: status,
                labelKey: getTranslation({context: TranslationContext.TERMINATION_STATUS, value: status, t}),
            })),
            type: FilterType.ENUM,
        },
        {
            fieldName: TerminationField.STATUS_CHANGED_AT,
            filterOperators: [FilterOperator.LT, FilterOperator.GT, FilterOperator.LTE, FilterOperator.GTE],
            type: FilterType.DATE_TIME,
        },
        {
            fieldName: TerminationField.MAIL_PENDING,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.values(TerminationMailType).map((mailType) => ({
                value: mailType,
                labelKey: getTranslation({context: TranslationContext.TERMINATION_MAIL_TYPE, value: mailType, t}),
            })),
            type: FilterType.ENUM,
        },
    ];
    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="terminationsList"
                queryHook={useTerminationListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_terminations")}
            />
        </Page>
    );
}
